import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Download, Lock, RefreshCw, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { API, BRAND } from "@/lib/config";
import { useMeta } from "@/hooks/useMeta";

const CREDS_KEY = "yn.admin";

function saveCreds(u, p) {
  try {
    sessionStorage.setItem(CREDS_KEY, btoa(`${u}:${p}`));
  } catch (_e) {
    /* ignore */
  }
}
function readCreds() {
  try {
    return sessionStorage.getItem(CREDS_KEY);
  } catch (_e) {
    return null;
  }
}
function clearCreds() {
  try {
    sessionStorage.removeItem(CREDS_KEY);
  } catch (_e) {
    /* ignore */
  }
}

function authHeaders() {
  const b64 = readCreds();
  return b64 ? { Authorization: `Basic ${b64}` } : {};
}

/* , Login , */
function AdminLogin({ onOk }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const b64 = btoa(`${u}:${p}`);
      const res = await axios.get(`${API}/admin/leads?page=1&limit=1`, {
        headers: { Authorization: `Basic ${b64}` },
        validateStatus: () => true,
      });
      if (res.status === 200) {
        saveCreds(u, p);
        onOk();
      } else if (res.status === 401) {
        setErr("Invalid username or password.");
      } else if (res.status === 503) {
        setErr("Admin is not configured on the server.");
      } else {
        setErr(`Unexpected response (${res.status}).`);
      }
    } catch (e2) {
      setErr(e2?.message || "Network error.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-[color:var(--yn-border)] p-6 sm:p-8 bg-white"
      >
        <div className="flex items-center gap-2 mb-6">
          <Lock size={18} style={{ color: "var(--yn-primary)" }} />
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Admin sign in
          </p>
        </div>
        <h1 className="font-heading text-2xl font-bold text-[color:var(--yn-ink)]">
          Leads dashboard
        </h1>
        <p className="mt-2 text-sm text-[color:var(--yn-muted)]">
          Read-only view of {BRAND.name} leads. Credentials are held in
          this browser session only.
        </p>

        <div className="mt-6 space-y-3">
          <div>
            <label htmlFor="admin-user" className="block text-sm font-medium text-[color:var(--yn-ink)] mb-1.5">
              Username
            </label>
            <Input id="admin-user" value={u} onChange={(e) => setU(e.target.value)} required autoFocus autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-pass" className="block text-sm font-medium text-[color:var(--yn-ink)] mb-1.5">
              Password
            </label>
            <Input id="admin-pass" type="password" value={p} onChange={(e) => setP(e.target.value)} required autoComplete="current-password" />
          </div>
        </div>

        {err && <p className="mt-4 text-sm text-red-600" role="alert">{err}</p>}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full inline-flex items-center justify-center h-11 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] disabled:opacity-70"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/* , Dashboard , */
function AggregatePanel({ title, rows }) {
  const total = rows.reduce((n, r) => n + r.count, 0) || 1;
  return (
    <div className="rounded-2xl border border-[color:var(--yn-border)] p-5 bg-white">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)] mb-4">
        {title}
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--yn-muted)]">No data yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {rows.slice(0, 8).map((r) => (
            <li key={r.value} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[color:var(--yn-ink)] truncate" title={r.value}>{r.value}</span>
                <span className="tabular-nums font-medium text-[color:var(--yn-muted)]">
                  {r.count}
                </span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-[color:var(--yn-subtle)] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(r.count / total) * 100}%`, background: "var(--yn-primary)" }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminLeads() {
  useMeta({ title: "Admin · Leads, YeboNadi", description: "" });
  const [authed, setAuthed] = useState(!!readCreds());
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        params: { page, limit },
        headers: authHeaders(),
        validateStatus: () => true,
      });
      if (res.status === 401) {
        clearCreds();
        setAuthed(false);
        return;
      }
      if (res.status !== 200) {
        setErr(`Server returned ${res.status}`);
        return;
      }
      setData(res.data);
    } catch (e) {
      setErr(e?.message || "Network error");
    } finally {
      setBusy(false);
    }
  }, [page, limit]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads.csv`, {
        headers: authHeaders(),
        responseType: "blob",
        validateStatus: () => true,
      });
      if (res.status === 401) {
        clearCreds();
        setAuthed(false);
        return;
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yebonadi-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.message || "Download failed");
    }
  };

  const signOut = () => {
    clearCreds();
    setAuthed(false);
    setData(null);
  };

  if (!authed) return <AdminLogin onOk={() => setAuthed(true)} />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Admin
          </p>
          <h1 className="mt-1 font-heading text-3xl font-bold text-[color:var(--yn-ink)]">
            Leads dashboard
          </h1>
          <p className="mt-1 text-sm text-[color:var(--yn-muted)]">
            {data ? `${data.total} total leads` : "…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium text-[color:var(--yn-ink)] border border-[color:var(--yn-border)] bg-white hover:bg-[color:var(--yn-subtle)]">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
            Refresh
          </button>
          <button onClick={downloadCsv} className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)]">
            <Download size={14} />
            Export CSV
          </button>
          <button onClick={signOut} className="inline-flex items-center gap-2 h-10 px-3 rounded-md text-sm font-medium text-[color:var(--yn-muted)] hover:text-[color:var(--yn-ink)]" title="Sign out">
            <LogOut size={14} />
          </button>
        </div>
      </div>

      {err && <p className="mt-4 text-sm text-red-600">{err}</p>}

      {data && (
        <>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <AggregatePanel title="By source" rows={data.aggregates.source} />
            <AggregatePanel title="By variant" rows={data.aggregates.variant} />
            <AggregatePanel title="By utm_source" rows={data.aggregates.utm_source} />
            <AggregatePanel title="By utm_campaign" rows={data.aggregates.utm_campaign} />
          </div>

          <div className="mt-10 rounded-2xl border border-[color:var(--yn-border)] bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[color:var(--yn-subtle)] text-[color:var(--yn-muted)]">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">Time</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Source</th>
                    <th className="px-4 py-3 font-semibold">Page</th>
                    <th className="px-4 py-3 font-semibold">Variant</th>
                    <th className="px-4 py-3 font-semibold">UTM source</th>
                    <th className="px-4 py-3 font-semibold">UTM campaign</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--yn-border)]">
                  {data.leads.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[color:var(--yn-muted)]">No leads yet.</td></tr>
                  ) : (
                    data.leads.map((l) => (
                      <tr key={l.id} className="text-[color:var(--yn-ink)]">
                        <td className="px-4 py-3 whitespace-nowrap text-[color:var(--yn-muted)] tabular-nums">
                          {new Date(l.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.email}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.source || ","}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.page || ","}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.variant || ","}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.utm_source || ","}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{l.utm_campaign || ","}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t border-[color:var(--yn-border)] flex items-center justify-between text-sm">
              <p className="text-[color:var(--yn-muted)]">
                Page {data.page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[color:var(--yn-border)] text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-subtle)] disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[color:var(--yn-border)] text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-subtle)] disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
